import { useRef } from 'react';
import { css } from '@emotion/css';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import BrighteMarkIcon from '../../../assets/brighte-mark.svg?react';
import { useAssistant } from './AssistantProvider';
import { ATTENTION_THRESHOLD, PROACTIVE_OFFER_COPY } from './constants';
import { useDialogDismissShield } from './useDialogDismissShield';

const launcherContainer = css`
  position: fixed;
  pointer-events: auto;
  right: 24px;
  bottom: 24px;
  z-index: 250;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`;

const launcherButton = css`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 1px solid #dce1ec;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  color: #00c28c;
`;

const launcherAttention = css`
  background: #00d299;
  border-color: #00d299;
  /* The mark inherits currentColor — it stays white on green, never the dark brand mark. */
  color: #ffffff;
  @media (prefers-reduced-motion: no-preference) {
    animation: assistant-pulse 1.2s ease-out 1;
  }
  @keyframes assistant-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 210, 153, 0.5);
    }
    100% {
      box-shadow: 0 0 0 16px rgba(0, 210, 153, 0);
    }
  }
`;

const offerCard = css`
  width: 260px;
`;

export function AssistantLauncher() {
  const { isOpen, stepKey, struggleScore, offerVisible, open, dismissOffer } = useAssistant();
  const rootRef = useRef<HTMLDivElement>(null);
  useDialogDismissShield(rootRef);

  if (isOpen) {
    return null;
  }

  const hasAttention = struggleScore >= ATTENTION_THRESHOLD;
  const offerCopy = (stepKey && PROACTIVE_OFFER_COPY[stepKey]) || 'Need a hand with this step? I can help.';

  return (
    <div ref={rootRef} className={launcherContainer}>
      {offerVisible ? (
        <Box
          className={offerCard}
          background="surface"
          border="primary"
          borderRadius="large"
          padding="medium"
          data-testid="assistant-proactive-offer"
        >
          <Stack gap="small">
            <Text size="small">{offerCopy}</Text>
            <Box display="flex" gap="small">
              <Button onClick={open}>Get help</Button>
              <Button tone="neutral" prominence="low" onClick={dismissOffer}>
                Dismiss
              </Button>
            </Box>
          </Stack>
        </Box>
      ) : null}
      <button
        type="button"
        aria-label={hasAttention ? 'Open Brighte Assistant — help available' : 'Open Brighte Assistant'}
        data-testid="assistant-launcher"
        data-attention={hasAttention || undefined}
        className={`${launcherButton} ${hasAttention ? launcherAttention : ''}`}
        onClick={open}
      >
        <BrighteMarkIcon width={24} height={24} />
      </button>
    </div>
  );
}
